import Vue from 'vue';
import VueI18n from 'vue-i18n';
import defaultLocale from './locales/en-US.json';
import axios from 'axios';
import { Quasar } from 'quasar';
import { compass } from '@resclick/compass';

Vue.use(VueI18n);

export const i18n = new VueI18n({
  locale: 'en-US', // set locale
  fallbackLocale: 'en-US',
  messages: {
    'en-US': defaultLocale,
  }, // set locale messages
});

const loadedLanguages = ['en-US']; // our default language that is preloaded

function setI18nLanguage(lang: string) {
  i18n.locale = lang;
  axios.defaults.headers.common['Accept-Language'] = lang;
  (document.querySelector('html') as HTMLElement).setAttribute('lang', lang);
  compass.setLanguage(lang);
  return lang;
}

export function loadLanguageAsync(lang: string) {
  // If the same language
  if (i18n.locale === lang) {
    return Promise.resolve(setI18nLanguage(lang));
  }

  // If the language was already loaded
  if (loadedLanguages.includes(lang)) {
    return Promise.resolve(setI18nLanguage(lang));
  }

  // If the language hasn't been loaded yet
  return import(/*  webpackChunkName: "lang-[request]" */ `@/locales/${lang}`).then((messages) => {
    i18n.setLocaleMessage(lang, messages.default);
    loadedLanguages.push(lang);
    return setI18nLanguage(lang);
  });
}

export async function setLanguage(input: string) {
  if (input.includes('-')) {
    const keys = input.split('-');
    input = `${keys[0]}-${keys[1].toUpperCase()}`;
  }

  // Not found packs
  // Azer, Hindi, Armenian, Uzbek, Kazakh
  const languageMap: any = {
    'en-US': 'en-us',
    'tr-TR': 'tr',
    'az-AZ': 'tr',
    'kz-KK': 'en-us',
    'kk-KZ': 'ru',
    'uz-UZ': 'en-us',
    'hi-IN': 'en-us',
    'hy-AM': 'en-us',
    'de-DE': 'de',
    'es-ES': 'es',
    'fr-FR': 'fr',
    'it-IT': 'it',
    'pl-PL': 'pl',
    'pt-PT': 'pt',
    'ru-RU': 'ru',
    'zh-HK': 'zh-hans',
    'zh-CH': 'zh-hant',
    'el-GR': 'el',
    'cs-CZ': 'cs',
    'da-DK': 'da',
    'fi-FI': 'fi',
    'bg-BG': 'bg',
    'sv-SW': 'sv',
    'lt-LT': 'lt',
    'ar-SA': 'ar',
    'ar-AE': 'ar',
    'vi-VN': 'vi',
    'uk-UA': 'uk',
    'ko-KR': 'ko-kr',
    'he-IL': 'he',
    'jp-JA': 'ja',
    'sv-SE': 'sv',
  };

  let lang = 'en-US';
  if (languageMap[input]) lang = input;
  else {
    const result = Object.keys(languageMap).find((key) => languageMap[key] === input);
    if (result) lang = result;
  }

  const quasarLanguage = languageMap[lang];

  compass.setLanguage(lang);

  return import(
    /* webpackInclude: /(en-us|tr|de|es|fr|it|pl|pt|ru|zh-hans|zh-hant|el|cs|da|fi|bg|sv|lt|ar|vi|uk|ko-kr|he|ja|sv)\.js$/ */
    'quasar/lang/' + quasarLanguage
  )
    .then((lang) => Quasar.lang.set(lang.default))
    .finally(() => loadLanguageAsync(lang).then(() => (i18n.locale = lang)));
}
